import { useContext, useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { AuthContext } from '@/contexts/auth-context'
import axiosInstance from '@/api/axiosInstance'
import { generateCardHTML } from '@/config/cardGenerator'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { motion } from 'framer-motion'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  RiStarFill,
  RiSparkling2Fill,
  RiCheckboxCircleFill,
  RiRefreshLine,
  RiArrowLeftLine,
} from 'react-icons/ri'
import logger from '../../utils/logger'

function CardsListPage() {
  const { auth } = useContext(AuthContext)
  const [switchConfirmation, setSwitchConfirmation] = useState(false)
  const [cardToSwitch, setCardToSwitch] = useState(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { state } = useLocation()

  const businessName = state?.businessDetails?.business_name || null

  const { businessId } = useParams()

  const [scaledWidth, setScaledWidth] = useState(300) // default fallback

  const baseCardWidth = 700

  const calculateScale = () => {
    const width = window.innerWidth
    if (width >= 1024) return 0.6
    if (width >= 768) return 0.5
    return 0.46
  }

  const updateScaledWidth = () => {
    const scale = calculateScale()
    setScaledWidth(baseCardWidth * scale)
  }

  useEffect(() => {
    updateScaledWidth()
    window.addEventListener('resize', updateScaledWidth)
    return () => window.removeEventListener('resize', updateScaledWidth)
  }, [])

  // Fetch cards
  const { data, isPending, error } = useQuery({
    queryKey: ['customerBusinessCards', auth.user._id, businessId],
    queryFn: async () => {
      const response = await axiosInstance.get('web-api/customer-business-cards', {
        params: {
          businessId,
          userId: auth.user._id,
        },
      })
      return response.data
    },
  })

  // Switch card mutation
  const switchMutation = useMutation({
    mutationFn: (userCardId) => axiosInstance.post('web-api/switch-active-card', { userCardId }),
    onSuccess: () => {
      toast.success('Card switched successfully', {
        icon: <RiCheckboxCircleFill className="text-green-500 text-xl" />,
        description: 'Your active card has been updated',
        action: {
          label: 'Dismiss',
          onClick: () => {},
        },
      })
      queryClient.invalidateQueries(['customerBusinessCards'])
      setSwitchConfirmation(false)
      navigate(-1) // Navigate back after successful switch
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error switching card', {
        icon: <RiCheckboxCircleFill className="text-red-500 text-xl" />,
      })
    },
  })

  const handleSwitchCard = (card) => {
    if (card.isActive) {
      toast.info('This card is already active', {
        icon: <RiStarFill className="text-purple-500 text-xl" />,
      })
      return
    }
    setCardToSwitch(card)
    setSwitchConfirmation(true)
  }

  const handleConfirmSwitch = async () => {
    if (cardToSwitch) {
      await switchMutation.mutateAsync(cardToSwitch._id)
    }
  }

  logger.log('switch mutation ===========> ', switchMutation)
  // Loading skeleton with improved animation
  if (isPending) {
    return (
      <div className="container mx-auto p-4 space-y-6">
        <motion.h1
          initial={{ opacity: 0.5 }}
          animate={{ opacity: 1 }}
          transition={{ repeat: Infinity, duration: 1.5, repeatType: 'reverse' }}
          className="text-2xl font-bold text-gray-900 bg-gradient-to-r from-purple-100 to-gray-100 w-64 h-8 rounded"
        >
          &nbsp;
        </motion.h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map((n) => (
            <motion.div
              key={n}
              initial={{ opacity: 0.5 }}
              animate={{ opacity: 1 }}
              transition={{
                repeat: Infinity,
                duration: 1.5,
                repeatType: 'reverse',
                delay: n * 0.2,
              }}
              className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden shadow-sm"
            >
              <div className="relative w-full h-[220px]">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg mb-4" />
              </div>
              <div className="p-4">
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    )
  }

  // Error state with better UI
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="container mx-auto p-4"
      >
        <div className="text-center py-12 bg-gradient-to-br from-red-50 to-pink-50 rounded-xl shadow-inner">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <RiSparkling2Fill className="text-red-500 text-2xl" />
          </div>
          <p className="text-red-600 font-medium text-lg">Error loading cards</p>
          <p className="text-gray-500 mt-2">Please try again later</p>
          <Button
            variant="outline"
            className="mt-4 border-red-200 text-red-500 hover:bg-red-50"
            onClick={() => queryClient.refetchQueries(['customerBusinessCards'])}
          >
            Retry
          </Button>
        </div>
      </motion.div>
    )
  }

  // No cards or single card state with improved UI
  if (!data?.data?.cards || data.data.cards.length <= 1) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="container mx-auto p-4"
      >
        <div className="text-center py-12 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-inner">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <RiStarFill className="text-purple-500 text-2xl" />
          </div>
          <p className="text-purple-600 font-medium text-lg">
            {data?.data?.cards?.length === 1
              ? 'Only one card available'
              : 'No additional cards available'}
          </p>
          <p className="text-gray-500 mt-2">
            {data?.data?.cards?.length === 1
              ? 'This business currently offers just one loyalty card. Switching is not applicable.'
              : "This business hasn't added multiple cards yet."}
          </p>
        </div>
      </motion.div>
    )
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center gap-2 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="hover:bg-gray-100 rounded-full p-2 transition-all"
        >
          <RiArrowLeftLine className="w-5 h-5 text-gray-600" />
        </Button>
        <h3 className="text-lg font-semibold text-gray-900">
          Switch card {businessName ? `for store : ${businessName}` : ''}
        </h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data?.data?.cards.map((card) => (
          <div
            key={card?._id}
            className={cn(
              'flex flex-col items-center w-full justify-center sm:border border-dashed rounded-xl sm:rounded-2xl sm:shadow py-2 pt-0 sm:pt-4 sm:pb-2',
              card.isActive
                ? 'sm:bg-gradient-to-br from-pink-200 via-sky-100 to-blue-200'
                : 'sm:bg-white'
            )}
          >
            {/* Card Container */}
            <div className="relative w-[300px] md:w-[490px] lg:w-[490px] h-[160px] md:h-[180px] lg:h-[210px] flex items-center justify-center">
              <div
                className="transform scale-[0.46] md:scale-[0.5] lg:scale-[0.6]"
                style={{ width: `${baseCardWidth}px`, height: '350px' }}
                dangerouslySetInnerHTML={{ __html: generateCardHTML(card?.card) }}
              />
            </div>

            {/* Switch Button Container */}
            <div
              className="mx-auto px-2 flex items-center justify-center mt-2"
              style={{ width: `${scaledWidth}px` }}
            >
              {card.isActive ? (
                <div className="flex items-center gap-1 py-1 bg-gradient-to-r from-green-50 to-emerald-50 px-4 rounded-xl shadow-sm border border-green-100">
                  <RiCheckboxCircleFill className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-green-600">Active Card</span>
                </div>
              ) : (
                <Button
                  onClick={() => handleSwitchCard(card)}
                  className="bg-purple-600 cursor-pointer hover:bg-purple-700 text-white font-medium px-6 py-1 rounded-xl shadow-sm flex items-center gap-2"
                >
                  <RiRefreshLine className="w-2 md:w-4 h-2 md:h-4" />
                  Switch to this Card
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Enhanced Dialog with animations */}
      <Dialog open={switchConfirmation} onOpenChange={setSwitchConfirmation}>
        <DialogContent className="sm:max-w-md rounded-lg overflow-hidden">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-red-50 to-white"
          >
            <DialogHeader className="px-6 pt-6 pb-4">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-2 rounded-full">
                  <RiRefreshLine className="text-red-600 text-xl" />
                </div>
                <div>
                  <DialogTitle className="text-red-600">Are you sure?</DialogTitle>
                  <DialogDescription className="mt-1 text-red-500 font-medium">
                    Switching cards will permanently reset the progress on your current active card.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="px-6 py-4 bg-white border-t border-b border-gray-100">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                    <RiStarFill className="text-red-500" />
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Warning</p>
                  <p className="text-sm text-gray-600 mt-1">
                    This action is irreversible. Once you activate another card, current progress of
                    your current active card will be lost permanently.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter className="px-6 pb-6 pt-4 bg-gray-50">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex gap-3"
              >
                <Button
                  variant="outline"
                  onClick={() => setSwitchConfirmation(false)}
                  disabled={switchMutation.isPending}
                  className="flex-1 border-gray-300 hover:bg-gray-100"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmSwitch}
                  disabled={switchMutation.isPending}
                  className="flex-1 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 shadow-red-200 hover:shadow-red-300 shadow-sm"
                >
                  {switchMutation.isPending ? (
                    <span className="flex items-center gap-2">
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="inline-block"
                      >
                        <RiRefreshLine />
                      </motion.span>
                      Switching...
                    </span>
                  ) : (
                    'Confirm Switch'
                  )}
                </Button>
              </motion.div>
            </DialogFooter>
          </motion.div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CardsListPage
